import { useWorkspaceText } from './interface-copy';
import { useContext, useEffect, useState } from 'react';
import { Dialog, Popover } from 'radix-ui';
import { MoreHorizontal } from 'lucide-react';
import { InChatActionsMenu } from '@/mobile/chat-actions';
import { companyRequest, CompanyRequestError } from '@/company/api';
import { useWorkspace } from './context';
import { WorkspaceIcon } from './icons';
import { WorkspaceMarkdown } from './markdown';
import { ShareButton } from './sharing';
import type { CompanyResult } from './types';
import { useIntlLocale } from './intl-locale';
import { namespaceCopyFor, useNamespaceCopy } from '@/i18n/ns';
import connectionsActionsCopy from '@/i18n/ns/connections-actions';
import sharingCopy from '@/i18n/ns/sharing-connectors';

type Snapshot = { title: string; markdown: string; hash: string; messageCount: number };
// The chat itself is saved by default and shared with Share (collaboration v1). A snapshot of its text can still be
// saved as a Result in Library, but that is not a chat-header action: it lives in the ⋯ menu (inline inside the narrow
// actions menu, which is already a menu), under a label that says what it saves. Offered on the person's own saved
// chat: listed, or described by the server as theirs (a fresh fork).
export function ShareChatButton() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const actionCopy = useNamespaceCopy(connectionsActionsCopy);
  const ws = useWorkspace();
  const nested = useContext(InChatActionsMenu);
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const own =
    ws.threads.some((thread) => thread.id === ws.threadId) ||
    (ws.threadMeta?.id === ws.threadId && ws.threadMeta.access === 'owner');
  if (!ws.threadId || !own) return null;
  const choose = () => {
    setMenu(false);
    setOpen(true);
  };
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      {nested ? (
        <button type="button" className="ws-text-button" onClick={choose}>
          {actionCopy.saveSnapshot}
        </button>
      ) : (
        <Popover.Root open={menu} onOpenChange={setMenu}>
          <Popover.Trigger asChild>
            <button
              type="button"
              className="ws-icon-button"
              aria-label={uiText('More chat actions')}
            >
              <MoreHorizontal size={20} aria-hidden="true" />
            </button>
          </Popover.Trigger>
          <Popover.Content align="end" sideOffset={8} className="ws-chat-more-menu">
            <button type="button" className="ws-text-button" onClick={choose}>
              {actionCopy.saveSnapshot}
            </button>
          </Popover.Content>
        </Popover.Root>
      )}
      {open && <ChatSnapshotDialog key={ws.threadId} threadId={ws.threadId} />}
    </Dialog.Root>
  );
}

function ChatSnapshotDialog({ threadId }: { threadId: string }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const actionCopy = useNamespaceCopy(connectionsActionsCopy);
  const ws = useWorkspace();
  const locale = useIntlLocale();
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [result, setResult] = useState<CompanyResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const currentResult = ws.data.results.find((item) => item.id === result?.id) ?? result;
  const endpoint = `/api/v1/history/chats/${encodeURIComponent(threadId)}/snapshot`;
  useEffect(() => {
    let active = true;
    setSnapshot(null);
    setError('');
    void companyRequest<Snapshot>(endpoint)
      .then((value) => {
        if (active) setSnapshot(value);
      })
      .catch((failure) => {
        if (active) setError(snapshotError(failure, locale));
      });
    return () => {
      active = false;
    };
  }, [endpoint, reload, locale]);
  async function save() {
    if (!snapshot) return;
    setBusy(true);
    setError('');
    try {
      const saved = await companyRequest<{ result: CompanyResult }>(endpoint, 'POST', {
        confirmed: true,
        hash: snapshot.hash,
      });
      // Read back the access projection, rather than assuming creation granted sharing.
      const readback = await companyRequest<{ result: CompanyResult }>(
        `/api/v1/company/results/${encodeURIComponent(saved.result.id)}`,
      );
      setResult(readback.result);
      await ws.refresh();
    } catch (failure) {
      setError(snapshotError(failure, locale));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog.Portal>
      <Dialog.Overlay className="ws-finder-scrim" />
      <Dialog.Content className="ws-share-dialog">
        <div className="ws-share-heading">
          <Dialog.Title>{actionCopy.saveSnapshot}</Dialog.Title>
          <Dialog.Close
            className="ws-icon-button"
            aria-label={uiText('Close conversation sharing')}
          >
            <WorkspaceIcon name="close" />
          </Dialog.Close>
        </div>
        <Dialog.Description>
          {uiText(
            'Review the text before saving a private copy to Library. Then choose its audience. Your original conversation and future messages stay private.',
          )}
        </Dialog.Description>
        {snapshot && !result && (
          <div className="ws-review-preview">
            <WorkspaceMarkdown text={snapshot.markdown} />
          </div>
        )}
        {!snapshot && !error && <p role="status">{uiText('Preparing the conversation…')}</p>}
        {error && (
          <p className="ws-error" role="alert">
            {error}{' '}
            <button className="ws-text-button" onClick={() => setReload((value) => value + 1)}>
              {uiText('Refresh preview')}
            </button>
          </p>
        )}
        {currentResult ? (
          <>
            <p role="status">
              {uiText('The copy is saved in Library. Use Share to choose who can access it.')}
            </p>
            <ShareButton kind="result" item={currentResult} />
          </>
        ) : (
          <button
            className="ws-button primary"
            disabled={!snapshot || busy}
            onClick={() => void save()}
          >
            {busy ? uiText('Saving…') : uiText('Save this copy')}
          </button>
        )}
      </Dialog.Content>
    </Dialog.Portal>
  );
}
function snapshotError(error: unknown, locale?: string) {
  const copy = namespaceCopyFor(sharingCopy, locale);
  if (error instanceof CompanyRequestError) {
    if (error.code === 'chat_snapshot_changed') return copy.snapshotChanged;
    if (error.code === 'chat_snapshot_too_large') return copy.snapshotTooLarge;
    if (error.code === 'chat_snapshot_empty') return copy.snapshotEmpty;
  }
  return copy.snapshotGeneric;
}
